import type { ButtonHTMLAttributes, ReactNode } from 'react'
import React, { forwardRef } from 'react'
import { cn } from '@utils/safeTs'
import styles from './ButtonRounded.module.sass'

export type ButtonRoundedSize = 'sm' | 'md'

export type ButtonRoundedBadge =
  | {
      type: 'dot'
      ariaLabel?: string
    }
  | {
      type: 'number'
      value: number
      ariaLabel?: string
    }

export interface ButtonRoundedProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  label: string
  size?: ButtonRoundedSize
  icon?: ReactNode | null
  badge?: ButtonRoundedBadge
  ariaLabel?: string
  iconPos?: 'left' | 'right'
}

const ButtonRounded = forwardRef<HTMLButtonElement, ButtonRoundedProps>(
  (
    {
      label,
      size = 'md',
      icon = undefined,
      badge,
      className,
      ariaLabel,
      iconPos = 'right',
      type = 'button',
      disabled,
      ...rest
    },
    ref,
  ) => {

    const badgeAnnouncement =
      badge?.ariaLabel ?? (badge?.type === 'number' ? `${badge.value}` : undefined)

    const finalAriaLabel = ariaLabel ?? (badgeAnnouncement ? `${label}, ${badgeAnnouncement}` : undefined)

    const renderIcon = () =>
      icon ? (
        <span className={styles.icon} aria-hidden>
          {icon}
        </span>
      ) : null

    return (
      <button
        ref={ref}
        type={type}
        className={cn(styles.root, styles[`root--${size}`], badge?.type ? styles[`root--badge-${badge.type}`] : undefined, className)}
        aria-label={finalAriaLabel}
        disabled={disabled}
        {...rest}
      >
        {iconPos === 'left' ? renderIcon() : null}
        <span className={styles.label}>{label}</span>
        {iconPos === 'right' ? renderIcon() : null}
        {!!badge && (
          <span className={styles.badge} aria-hidden>{badge?.type === 'number' ? badge.value : <></>}</span>
        )}
      </button>
    )
  },
)

export default ButtonRounded
